import { styled, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import React from 'react';
import { RotatingLines } from 'react-loader-spinner';

import CommonTooltip from '../common-tooltip/CommonTooltip';

function CommonTabs({
  tabs,
  value,
  onChange,
  isFullWidth = false,
  isContainerFullWidth = true,
  classes = EMPTY_CLASSES,
  appearance = TABS_APPEARANCE.OUTLINED,
  ...rest
}) {
  const isMobile = useMediaQuery('(max-width: 600px)');
  const isFilled = appearance === TABS_APPEARANCE.FILLED;

  return (
    <Box className={classes.wrapper}>
      <StyledTabs
        isFilled={isFilled}
        isContainerFullWidth={isContainerFullWidth}
        value={value}
        onChange={onChange}
        className={classes.tabList}
        variant={isFullWidth && !isMobile ? 'fullWidth' : 'scrollable'}
        scrollButtons={'auto'}
        {...rest}>
        {tabs.map((tab, idx) => (
          <CommonTooltip key={idx} title={tab.tooltip}>
            <StyledTab
              isFilled={isFilled}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {tab.label}
                  {tab.isLoading && <RotatingLines strokeColor='#fdbf47' height={10} width={20} />}
                </Box>
              }
              disabled={tab.disabled}
              className={classes.tab}
              disableRipple
            />
          </CommonTooltip>
        ))}
      </StyledTabs>
    </Box>
  );
}

export default CommonTabs;

const StyledTab = styled(Tab, {
  shouldForwardProp: (prop) => prop !== 'isFilled',
})(({ isFilled }) => ({
  fontSize: '10px',
  lineHeight: '12px',
  letterSpacing: '2px',
  fontWeight: 'bold',
  color: 'var(--text-default)',
  textTransform: 'uppercase',
  fontFamily: 'Inter',
  transition: isFilled
    ? 'color 0.2s ease, background-color 0.2s ease, transform 0.18s ease, box-shadow 0.18s ease'
    : 'color 0.2s ease',
  borderRadius: isFilled ? '8px' : 0,
  marginRight: isFilled ? '6px' : 0,

  minHeight: isFilled ? '32px' : 'unset',
  padding: isFilled ? '6px 12px' : undefined,
  border: isFilled ? '1px solid transparent' : 'none',
  transform: isFilled ? 'translateY(0) scale(1)' : 'none',
  transformOrigin: isFilled ? 'center' : 'unset',
  position: isFilled ? 'relative' : 'static',

  '&:last-of-type': {
    marginRight: 0,
  },

  '&:hover': {
    backgroundColor: isFilled ? 'var(--tabs-border)' : 'transparent',
    transform: isFilled ? 'translateY(0) scale(1)' : 'none',
    boxShadow: isFilled ? '0 4px 10px rgba(0, 0, 0, 0.15)' : 'none',
  },

  '&.Mui-selected': {
    color: isFilled ? 'var(--color-primary)' : 'var(--text-stressed)',
    borderBottom: isFilled ? 'none' : '2px solid var(--color-primary)',
    backgroundColor: isFilled ? 'var(--tabs-border)' : 'transparent',
    boxShadow: isFilled ? '0 4px 12px rgba(0, 0, 0, 0.2)' : 'none',
  },
  '&.Mui-disabled': {
    color: 'var(--text-default)',
    cursor: 'not-allowed',
    pointerEvents: 'auto !important',
    boxShadow: 'none',
  },
  '&.MuiButtonBase-root': {
    minHeight: 'unset !important',
  },
  '&.Mui-disabled.MuiButtonBase-root:hover': {
    cursor: 'not-allowed',
    transform: isFilled ? 'translateY(0) scale(1)' : 'none',
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  '&:not(:last-of-type)::after': {
    content: isFilled ? '""' : 'none',
    position: 'absolute',
    right: '-3px',
    top: '24%',
    height: '52%',
    width: '1px',
    backgroundColor: 'var(--tabs-border)',
    opacity: 0.8,
  },
}));

const StyledTabs = styled(Tabs, {
  shouldForwardProp: (prop) => prop !== 'isFilled' && prop !== 'isContainerFullWidth',
})(({ isFilled, isContainerFullWidth }) => ({
  border: isFilled ? '1px solid var(--tabs-border)' : 'none',
  borderBottom: '1px solid var(--tabs-border)',
  minHeight: 'unset !important',
  padding: isFilled ? '0px' : 0,
  borderRadius: isFilled ? '10px' : 0,
  backgroundColor: isFilled ? 'var(--background-tertiary, transparent)' : 'transparent',
  width: isContainerFullWidth ? '100%' : 'fit-content',
  maxWidth: '100%',

  '& .MuiTabs-indicator': {
    backgroundColor: 'var(--color-primary)',
    display: isFilled ? 'none' : 'block',
  },
  '& .MuiTabs-flexContainer': {
    columnGap: isFilled ? '4px' : 0,
  },
}));

export const TABS_APPEARANCE = {
  OUTLINED: 'outlined',
  FILLED: 'filled',
};

const EMPTY_CLASSES = {
  wrapper: '',
  tabList: '',
  tab: '',
};
